import { useEffect } from 'react';

/**
 * useSafeAreaClass
 *
 * 這個 hook 會在網頁載入時動態偵測 `env(safe-area-inset-bottom)` 是否大於 0，
 * 如果是，代表目前瀏覽器的畫面確實有 safe-area（例如 iPhone 底部導覽條或瀏海區），
 * 就會在 <body> 加上 `has-safe-area` class，讓 CSS 可以根據這種環境進行樣式調整。
 *
 * 📌 為什麼不用裝置偵測？
 * - 裝置偵測（例如 isIOS）只能判斷使用者的設備型號，但無法保證該設備在當下的瀏覽環境中會顯示 safe-area。
 * - 即使是 iOS 裝置，在某些情境下（如 PWA 全螢幕模式、LIFF WebView、橫向模式）也可能沒有 safe-area，這時 `env(safe-area-inset-bottom)` 會是 0。
 * - 相反地，有些 Android 裝置也可能因為瀏海或系統設定出現 safe-area，但無法從裝置型號推論。
 *
 */
const useSafeAreaClass = (): void => {
	useEffect(() => {
		// 建立測試元素，抓取實際 env() 對應的高度
		const testEl = document.createElement('div');
		testEl.style.cssText = `
      position: absolute;
      bottom: 0;
      height: env(safe-area-inset-bottom);
      pointer-events: none;
      visibility: hidden;
    `;
		document.body.appendChild(testEl);

		const { height } = getComputedStyle(testEl);
		const numericHeight = parseFloat(height);

		if (numericHeight > 0) {
			document.body.classList.add('has-safe-area');
		}

		document.body.removeChild(testEl);
	}, []);
};

export default useSafeAreaClass;
